import { IconAlertCircle } from "@tabler/icons-react";
import { cva, type VariantProps } from "class-variance-authority";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const errorStateVariants = cva("flex items-center", {
  variants: {
    variant: {
      default: "flex-col justify-center py-16 gap-4",
      inline: "flex-wrap gap-2 py-1",
    },
  },
  defaultVariants: { variant: "default" },
});

interface ErrorStateProps extends VariantProps<typeof errorStateVariants> {
  message: string;
  onRetry?: () => void;
  isRetrying?: boolean;
}

export function ErrorState({
  message,
  onRetry,
  isRetrying = false,
  variant = "default",
}: ErrorStateProps) {
  const { t } = useTranslation();
  return (
    <div data-slot="error-state" data-variant={variant} className={errorStateVariants({ variant })}>
      <IconAlertCircle
        aria-hidden="true"
        className={cn(
          "shrink-0 text-destructive-foreground",
          variant === "inline" ? "size-4" : "size-10",
        )}
      />
      <p
        className={cn(
          "text-destructive-foreground",
          variant === "inline" ? "text-xs" : "text-sm max-w-md text-center",
        )}
      >
        {message}
      </p>
      {onRetry && (
        <Button
          variant="outline"
          size={variant === "inline" ? "xs" : "sm"}
          onClick={onRetry}
          loading={isRetrying}
        >
          {t("actions.retry")}
        </Button>
      )}
    </div>
  );
}
